import { useEffect, useRef } from "react";
import { Check } from "lucide-react";

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const HANDLES = [
  ["l", "left-0 top-1/2 h-10 w-2.5 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize"],
  ["r", "right-0 top-1/2 h-10 w-2.5 translate-x-1/2 -translate-y-1/2 cursor-ew-resize"],
  ["t", "left-1/2 top-0 h-2.5 w-10 -translate-x-1/2 -translate-y-1/2 cursor-ns-resize"],
  ["b", "left-1/2 bottom-0 h-2.5 w-10 -translate-x-1/2 translate-y-1/2 cursor-ns-resize"],
  ["tl", "left-0 top-0 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 cursor-nwse-resize"],
  ["tr", "right-0 top-0 h-3.5 w-3.5 translate-x-1/2 -translate-y-1/2 cursor-nesw-resize"],
  ["bl", "left-0 bottom-0 h-3.5 w-3.5 -translate-x-1/2 translate-y-1/2 cursor-nesw-resize"],
  ["br", "right-0 bottom-0 h-3.5 w-3.5 translate-x-1/2 translate-y-1/2 cursor-nwse-resize"],
];

// Drag-to-crop handles drawn over the (uncropped) video frame in the preview.
export const CropOverlay = ({ clip, geom, fitMode, onChange, onDone }) => {
  const frameRef = useRef(null);
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") { e.stopPropagation(); onDone?.(); } };
    window.addEventListener("keydown", k, true);
    return () => window.removeEventListener("keydown", k, true);
  }, [onDone]);
  const c = { l: 0, t: 0, r: 0, b: 0, ...(clip.crop || {}) };
  const { vw, vh, cw, ch } = geom;
  const sc = fitMode === "contain" ? Math.min(cw / vw, ch / vh) : Math.max(cw / vw, ch / vh);
  const fw = (vw * sc) / cw * 100, fh = (vh * sc) / ch * 100;

  const startDrag = (e, edge) => {
    e.preventDefault(); e.stopPropagation();
    const box = frameRef.current.getBoundingClientRect();
    const x0 = e.clientX, y0 = e.clientY, s0 = { ...c };
    const move = (ev) => {
      const dx = (ev.clientX - x0) / box.width, dy = (ev.clientY - y0) / box.height;
      const n = { ...s0 };
      if (edge === "move") {
        const w = 1 - s0.l - s0.r, h = 1 - s0.t - s0.b;
        n.l = clamp(s0.l + dx, 0, 1 - w); n.r = 1 - w - n.l;
        n.t = clamp(s0.t + dy, 0, 1 - h); n.b = 1 - h - n.t;
      } else {
        if (edge.includes("l")) n.l = clamp(s0.l + dx, 0, 0.9 - s0.r);
        if (edge.includes("r")) n.r = clamp(s0.r - dx, 0, 0.9 - s0.l);
        if (edge.includes("t")) n.t = clamp(s0.t + dy, 0, 0.9 - s0.b);
        if (edge.includes("b")) n.b = clamp(s0.b - dy, 0, 0.9 - s0.t);
      }
      Object.keys(n).forEach((k) => { n[k] = Math.round(n[k] * 1000) / 1000; });
      onChange?.(n);
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div className="absolute inset-0 z-20 overflow-hidden" data-testid="crop-overlay">
      <div ref={frameRef} className="absolute" style={{ left: `${(100 - fw) / 2}%`, top: `${(100 - fh) / 2}%`, width: `${fw}%`, height: `${fh}%` }}>
        <div onPointerDown={(e) => startDrag(e, "move")} data-testid="crop-box"
          className="absolute cursor-move border-2 border-[#00F0FF]"
          style={{ left: `${c.l * 100}%`, top: `${c.t * 100}%`, right: `${c.r * 100}%`, bottom: `${c.b * 100}%`, boxShadow: "0 0 0 9999px rgba(0,0,0,0.6)" }}>
          <div className="pointer-events-none absolute inset-0 grid grid-cols-3 grid-rows-3">
            {Array.from({ length: 9 }).map((_, i) => <div key={i} className="border border-white/15" />)}
          </div>
          {HANDLES.map(([k, cls]) => (
            <div key={k} onPointerDown={(e) => startDrag(e, k)} data-testid={`crop-handle-${k}`} className={`absolute rounded-sm border border-black/40 bg-[#00F0FF] ${cls}`} />
          ))}
        </div>
      </div>
      <button onClick={onDone} data-testid="crop-done-btn"
        className="absolute left-1/2 top-3 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-[#00F0FF] px-4 py-1.5 text-xs font-bold text-black shadow-lg hover:bg-[#00F0FF]/90"><Check size={13} /> Done cropping</button>
    </div>
  );
};
